import { useFormik } from 'formik';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useState, useEffect } from 'react';
import * as yup from 'yup';
import Box from '../Box';
import Button from '../buttons/Button';
import IconButton from '../buttons/IconButton';
import Divider from '../Divider';
import FlexBox from '../FlexBox';
import Icon from '../icon/Icon';
import TextField from '../text-field/TextField';
import Typography, { H3, H5, H6, SemiSpan, Small, Span } from '../Typography';
import { StyledSessionCard } from './SessionStyle';
import { signIn, useSession, getSession } from 'next-auth/react';
import { UsersApi } from 'pages/api';
import toast from 'react-hot-toast';

const ResetPassword: React.FC = () => {
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [tempPasswordVisibility, setTempPasswordVisibility] = useState(false);
  const router = useRouter();
  const errorMessage = router.query.errorMessage || null;

  const resetPassword = async (email, tempPass, newPass) => {
    try {
      const { status } = await UsersApi.resetPassword(email, tempPass, newPass);
      if (status === undefined) {
        throw new Error('Please check your email/current password.');
      }
      if (status === 201) {
        toast.success('Password successfully updated.', {
          position: 'top-center',
          style: { fontSize: '16px' },
        });
        router.push(`/login`);
      }
    } catch (error) {
      toast.error('Oops, something went wrong');
    }
  };

  const toggleTempPasswordVisibility = useCallback(() => {
    setTempPasswordVisibility((visible) => !visible);
  }, []);

  const togglePasswordVisibility = useCallback(() => {
    setPasswordVisibility((visible) => !visible);
  }, []);

  const handleFormSubmit = async (values) => {
    // Passing values to the credentials provider
    const { email, temporaryPassword, newPassword } = values;
    resetPassword(email, temporaryPassword, newPassword);
  };

  const { values, errors, touched, handleBlur, handleChange, handleSubmit } = useFormik({
    onSubmit: handleFormSubmit,
    initialValues,
    validationSchema: formSchema,
  });

  return (
    <StyledSessionCard mx="auto" my="2rem" boxShadow="large">
      <form className="content" onSubmit={handleSubmit}>
        <H3 textAlign="center" mb="0.5rem">
          Rest Password
        </H3>
        <H5 fontWeight="600" fontSize="12px" color="gray.800" textAlign="center" mb="2.25rem">
          Please contact customer support at support@teakita.my to get temporary password
        </H5>

        <TextField
          mb="0.75rem"
          name="email"
          placeholder="exmple@mail.com"
          label="Email"
          type="email"
          fullwidth
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.email || ''}
          errorText={touched.email && errors.email}
        />

        <TextField
          mb="1rem"
          name="temporaryPassword"
          placeholder="*********"
          autoComplete="on"
          type={tempPasswordVisibility ? 'text' : 'password'}
          label="Temporary Password"
          fullwidth
          endAdornment={
            <IconButton
              size="small"
              type="button"
              p="0.25rem"
              mr="0.25rem"
              color={tempPasswordVisibility ? 'gray.700' : 'gray.600'}
              onClick={toggleTempPasswordVisibility}
            >
              <Icon variant="small" defaultcolor="currentColor">
                {tempPasswordVisibility ? 'eye-alt' : 'eye'}
              </Icon>
            </IconButton>
          }
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.temporaryPassword || ''}
          errorText={touched.temporaryPassword && errors.temporaryPassword}
        />

        <TextField
          mb="1rem"
          name="newPassword"
          placeholder="*********"
          autoComplete="on"
          type={passwordVisibility ? 'text' : 'password'}
          label="New Password"
          fullwidth
          endAdornment={
            <IconButton
              size="small"
              type="button"
              p="0.25rem"
              mr="0.25rem"
              color={passwordVisibility ? 'gray.700' : 'gray.600'}
              onClick={togglePasswordVisibility}
            >
              <Icon variant="small" defaultcolor="currentColor">
                {passwordVisibility ? 'eye-alt' : 'eye'}
              </Icon>
            </IconButton>
          }
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.newPassword || ''}
          errorText={touched.newPassword && errors.newPassword}
        />

        <Button mb="1.65rem" variant="contained" color="secondary" type="submit" fullwidth>
          Submit
        </Button>

        {errorMessage && (
          <Box mb="1rem">
            <Divider width="200px" mx="auto" />
            <FlexBox justifyContent="center" mt="-14px">
              <Span color="text.muted" bg="body.paper" px="1rem">
                <Typography color="red">{errorMessage}</Typography>
              </Span>
            </FlexBox>
          </Box>
        )}

        <FlexBox justifyContent="center" mb="1.25rem">
          <SemiSpan>Don’t have account?</SemiSpan>
          <Link href="/signup">
            <a>
              <H6 ml="0.5rem" borderBottom="1px solid" borderColor="gray.900">
                Sign Up
              </H6>
            </a>
          </Link>
        </FlexBox>
      </form>
    </StyledSessionCard>
  );
};

const initialValues = {
  email: '',
  temporaryPassword: '',
  newPassword: '',
};

const formSchema = yup.object().shape({
  email: yup.string().email().required('This is required'),
  temporaryPassword: yup.string().required('This is required'),
  newPassword: yup.string().required('This is required'),
});

export default ResetPassword;
